import { Model } from 'app/model/backendmodel';
import { notImplementedFunction } from 'app/extension/api/common';
import { IAzureResource, IAzureResourceShadow, IDataSourceCredentialUrlResponse, ISOCDataLakeAnalyticsAccount } from 'app/interfaces/IAzureResource';
import { LoggerHelper } from 'app/logger/logger-helper';
import { LoggerService } from 'app/logger/logger.service';
import { Http, Response } from 'app/services/http';
import * as HttpUtil from 'app/shared/utils/http.utils';
import { convertObjectToString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { retryWhen } from 'rxjs/operators';
import { AuthConstants } from '../authentication/auth-constants';
import { AuthService } from '../authentication/auth.service';
import { BaseMetadataClient } from '../base-metadata-client';
import * as Constants from '../globals';
import { WorkspaceUrlInformationService } from '../workspace-url-information.service';
import { A365MetadataClient } from './a365-metadata-client';
import { A365WorkspaceCacheService } from './A365workspace-cache.service';
import { CrudEvent } from 'app/logger/logger.crud-event';
import { sleep } from 'app/shared/utils/sleep.util';
import { ResAndNameCorrectionUtils } from 'app/model/services/res-name-correction-utils';
import { IDDLItem, IResourceContext, isSyMsCDMDb } from 'shell-models-lib';
import { Inject, NgZone } from '@angular/core';

import { getAzureEnvironment } from 'environments/environment.util';
import { getCurrentEnvironment } from 'environments/environment.current';
import { EventRow } from 'app/logger/logger.event-row';
import { GlobalVariables } from '../globals';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';
import { getServiceInstance, RootApplication } from 'app/multi-contexts/modules/root/root.application';

export class A365DataplaneClient extends BaseMetadataClient {
    public className = 'A365DataplaneClient';
    protected apiVersion: string = Constants.WORKSPACE_APIVERSION;
    protected authArea: string = AuthConstants.ARTIFACTS_ENDPOINT;

    private get _workspaceId(): string {
        return this._workspaceCacheService.workspace ? this._workspaceCacheService.workspace.id : '';
    }

    constructor(
        @Inject(RESOURCE_CONTEXT_DATA) _contextData: IResourceContext,
        httpClient: Http,
        authService: AuthService,
        protected urlService: WorkspaceUrlInformationService,
        logger: LoggerService,
        readonly loggerHelper: LoggerHelper,
        private readonly _a365MainMetadataClient: A365MetadataClient,
        private readonly _workspaceCacheService: A365WorkspaceCacheService) {
        super(_contextData, httpClient, authService, urlService, logger, loggerHelper);
    }

    public getDataSourceCredentialUrl(lsName: string, sessionId: string): Promise<IDataSourceCredentialUrlResponse> {
        return this._a365MainMetadataClient.getDataSourceCredentialUrl(lsName, sessionId);
    }

    public getSparkHistoryEndpoint(baseUri: string): Promise<string> {
        return notImplementedFunction();
    }

    public getSparkHistoryServerVersion(workspace: Model.Workspace): Promise<string> {
        return notImplementedFunction();
    }

    public getSOCDataLakeAnalyticsAccount(workspace: Model.Workspace): Promise<ISOCDataLakeAnalyticsAccount> {
        return notImplementedFunction();
    }
    public async rename(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<Response> {
        // handle database entities rename
        if (resourceType === 'database' || Constants.subResourceTypeMap['database'].includes(resourceType)) {
            return Promise.resolve(null);
        }

        let status: number;
        return new Promise<Response>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = this.createSingleResourceUri(resourceType, name) + '/rename';
            this.httpClient.post(url, JSON.stringify(payload), { headers, params: this.getDefaultParams(null, this._getApiVersion(resourceType)) })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    status = resp.status;
                    if (status === 202) {
                        const finalResp = await this._pollForRename(resp);

                        if (finalResp.status === 200) {
                            // response body is not empty, then can apply .json() to get the body
                            if (!stringIsNullOrEmpty(finalResp.text())) {
                                const body = finalResp.json();
                                if (body && body.status === 'Failed') {
                                    throw finalResp;
                                }
                            }
                        } else {
                            throw finalResp;
                        }

                        resolve(finalResp);
                    } else {
                        resolve(resp);
                    }
                }).catch((reason) => {
                this.loggerHelper.logRequestError(this.className, 'rename: Error while calling url', reason, { renameResourceUrl: url });
                    status = reason.status;
                    reject(reason);
                }).then(() => {
                    this.logger.logEvent(new CrudEvent('rename', name || this.urlService.resourceName, resourceType, '', undefined, status.toString()));
                });
        });
    }

    public getBaseUri(): string {
        if (this._workspaceCacheService.workspace) {
            return this._workspaceCacheService.workspace.properties.connectivityEndpoints.dev;
        }
        return `${getAzureEnvironment(getCurrentEnvironment()).composedSynapseGatewayEndpoint(this.urlService.workspaceName)}/`;
    }

    public async getResource(resourceType?: Constants.ResourceType, resourceName?: string, omitErrorLog?: boolean): Promise<IAzureResource> {
        if (!resourceType && !resourceName) {
            if (this.urlService.workspaceName) {
                return await super.getResource('workspace', this.urlService.workspaceName, omitErrorLog);
            }
            return null;
        }
        return await super.getResource(resourceType, resourceName, omitErrorLog);
    }

    public async deleteResource(resourceType: Constants.ResourceType, resourceName: string, retryBadRequest?: boolean): Promise<Response> {
        const correctedResTypeAndName = ResAndNameCorrectionUtils.correctResTypeAndName(resourceType, resourceName);
        const response = await super.deleteResource(resourceType, correctedResTypeAndName.resourceName, retryBadRequest, correctedResTypeAndName.resourceType);

        if (response?.hasBody()) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let parsed: any;
            try {
                parsed = response.json();
            } catch (e) {
                // No valid json is ok.
                this.logger.log(this.className, `Failed to parse response: ${convertObjectToString(e)}`);
            }
            if (parsed?.status && parsed.status.toLowerCase() === 'failed') {
                parsed.error.status = 400; // we need this flag to parse message
                throw parsed.error;
            }
        }
        return response;
    }

    /**
     * List resources without content.
     * Currently only notebook supports this operation.
     */
    public async listResourceShadows(resourceType: Constants.ResourceType): Promise<IAzureResourceShadow[]> {
        switch (resourceType) {
            case 'notebook': {
                return this._listSummary('notebook');
            }
            default:
                return super.listResourceShadows(resourceType);
        }
    }

    /**
     * Gets the complete list of resources.
     * @param resourceType
     */
    public async listResources(resourceType: Constants.ResourceType, complexResourceType?: string, apiVersion?: string): Promise<IAzureResource[]> {
        if (resourceType === 'database' || Constants.subResourceTypeMap['database'].includes(resourceType)) {
            return await this._listDatabasesOrEntities(resourceType, complexResourceType, apiVersion);
        }
        return await super.listResources(resourceType, complexResourceType, apiVersion);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async getNotebooksFirstPage(basicResourceType: Constants.ResourceType, apiVersion?: string): Promise<any> {
        const headers = await this.getDefaultHeadersAsync();
        const url = `${super._createResourceBaseUri(basicResourceType)}WithoutOutput`;
        try {
            const resp = await this.httpClient.get(url, { headers, params: this.getDefaultParams(null, apiVersion || this._getApiVersion(basicResourceType)) })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise();
            return resp?.json()?.result;
        } catch (reason) {
            this.loggerHelper.logRequestError(this.className, 'getNotebooksFirstPage: Error while calling url', reason, { getNotebooksFirstPageUrl: url });
            throw reason;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async getFirstPage(resourceType: Constants.ResourceType, complexResourceType?: string, apiVersion?: string): Promise<any> {
        if (resourceType === 'database' || Constants.subResourceTypeMap['database'].includes(resourceType)) {
            return await this._listDatabasesOrEntities(resourceType, complexResourceType, apiVersion);
        } else if (resourceType === 'notebook') {
            return await this.getNotebooksFirstPage(resourceType, apiVersion);
        }
        return await super.getFirstPage(resourceType, complexResourceType, apiVersion);
    }

    public async create(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource> {
        if (resourceType === 'database') {
            return await this._updateDatabase(resourceType, name, payload) as IAzureResource;
        }
        if (Constants.subResourceTypeMap['database'].includes(resourceType)) {
            return await this._dummyUpdateEntitiesInDatabase(resourceType, name, payload) as IAzureResource;
        }
        return await super.create(resourceType, name, payload);
    }

    public async update(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource> {
        if (resourceType === 'database') {
            return await this._updateDatabase(resourceType, name, payload) as IAzureResource;
        }
        if (Constants.subResourceTypeMap['database'].includes(resourceType)) {
            return await this._dummyUpdateEntitiesInDatabase(resourceType, name, payload) as IAzureResource;
        }
        return await super.update(resourceType, name, payload);
    }

    public upgradeLinkedService(linkedServiceName: string): Promise<Response> {
        return notImplementedFunction();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async patch(resourceType: Constants.ResourceType, name: string, payload: {}, path?: string): Promise<any> {
        return new Promise<IAzureResource>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = `${this.getBaseUri()}${path}`;
            this.httpClient.patch(url, JSON.stringify(payload), { headers, params: this.getDefaultParams(null, this._getApiVersion('workspace')) })
                .pipe()
                .toPromise()
                .then(async (resp) => {
                    resolve(resp.json());
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, 'patch: Error while calling url', reason, { patchUrl: url });
                    reject(reason);
                });
        });
    }

    protected createSingleResourceUri(resourceType: Constants.ResourceType, resourceName: string): string {
        if (resourceType === 'workspace') {
            return `${getAzureEnvironment(getCurrentEnvironment()).composedSynapseGatewayEndpoint(this.urlService.workspaceName)}/workspace`;
        }
        return super.createSingleResourceUri(resourceType, resourceName);
    }

    protected _getApiVersion(resourceType: Constants.ResourceType): string {
        if (resourceType === 'database' || Constants.subResourceTypeMap['database'].includes(resourceType)) {
            return Constants.WORKSPACE_DATABASE_2021_04_01;
        }
        return super._getApiVersion(resourceType);
    }

    private async _listSummary(resourceType: Constants.ResourceType): Promise<IAzureResourceShadow[]> {
        // Currently this method only works for notebook.
        let url = `${this.getBaseUri()}/${resourceType}sWithoutOutput`;
        let result: IAzureResourceShadow[] = [];
        let pageCounter = 0;
        while (url) {
            pageCounter++;
            const headers = await this.getDefaultHeadersAsync();
            let response: Response | undefined;
            try {
                response = await this.httpClient
                    .get(url, { headers, params: this.getDefaultParams(null, this.apiVersion) })
                    .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                    .toPromise();
            } catch (err) {
                this.loggerHelper.logRequestError(this.className, '_listSummary: Error while calling url', err, { listUrl: url, pageCounter: String(pageCounter) });
                throw err;
            }
            const responseJson = response?.json()?.result || undefined;
            url = undefined;
            if (responseJson) {
                if (Array.isArray(responseJson.value) && responseJson.value.length) {
                    result = result.concat(responseJson.value);
                }
                if (responseJson.nextLink && typeof responseJson.nextLink === 'string') {
                    //TODO(mantingli): remove replace function when chaofan fixed
                    url = responseJson.nextLink.replace(/^https:\/\/([\w-]+\.)+[\w-]+(:[\d]+)?(\/[\w-./?%&=]*)?\?/, `${this.getBaseUri()}/${resourceType}sWithoutOutput?`);
                }
            }
        }
        for (const resource of result) {
            if (!resource.objectType) {
                resource.objectType = `Microsoft.Synapse/workspaces/${resourceType}s`;
            }
            // n.b. resource.etag does exist in the response.
        }
        return result;
    }

    private async _pollForRename(resp: Response): Promise<Response> {
        const locationHeader = resp.headers.get('location') || '';
        const asyncHeaderUrl = locationHeader;
        let firstPhaseWaitingCount = 12;

        while (resp.status === 202) {
            const headers = await this.getDefaultHeadersAsync();
            resp = await this.httpClient.get(asyncHeaderUrl, { headers, params: this.getDefaultParams(null, this.apiVersion) })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise();

            if (firstPhaseWaitingCount > 0) {
                // For the first 2 min, polling rename result every 10 sec.
                await sleep(10000);
                firstPhaseWaitingCount--;
            } else {
                // After the first 2 min, polling rename result every 30 sec.
                await sleep(30000);
            }
        }

        return resp;
    }

    private async _listDatabasesOrEntities(resourceType: Constants.ResourceType, complexResourceType?: string, apiVersion?: string): Promise<IAzureResource[]> {
        const allItems: IAzureResource[] = [];
        let continuationToken: string | undefined = undefined;
        const headers = await this.getDefaultHeadersAsync();
        const url = this._createResourceBaseUri(complexResourceType ? complexResourceType : resourceType);
    
        do {
            try {
                const additionalParams = { 'continuationToken': continuationToken };
                const resp = await this.httpClient.get(url, {
                    headers,
                    params: this.getDefaultParams(additionalParams, apiVersion || this._getApiVersion(resourceType))
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise();
    
                const response = resp.json();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const finalItems = resourceType !== 'database' || response.items == null ? response.items : response.items.filter((item: any) => isSyMsCDMDb(item));
                allItems.push(...finalItems);
                continuationToken = response.continuationToken;
            } catch (reason) {
                this.loggerHelper.logRequestError(this.className, '_listDatabasesOrEntities: Error while calling url', reason, { listUrl: url });
                throw reason;
            }
        } while (continuationToken);
    
        return allItems;
    }
    

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _updateDatabase(resourceType: Constants.ResourceType, name: string | null, payload: any): Promise<{}> {
        return new Promise<IAzureResource>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const usageInfoHeaderKey = 'x-ms-usageinfo';
            const synapseDatabaseTemplateUsageInfo = 'AzureSynapseDatabaseTemplate';
            headers.set(usageInfoHeaderKey, synapseDatabaseTemplateUsageInfo);
            const basicUrl = resourceType ? this._createResourceBaseUri(resourceType) : this.getBaseUri();
            const action = 'executeChangeWithValidation';
            const url = `${basicUrl}/${action}`;
            const apiVersion = GlobalVariables.useLakeDB2021Api ? Constants.WORKSPACE_DATABASE_2021_04_01 : Constants.WORKSPACE_APIVERSION;
            const realPayload = payload.payload;
            const additionalParams = { upsert: true };
            const params: { [param: string]: string | string[] } = { ...this.getDefaultParams(additionalParams, apiVersion) };
            params.validationtype = 'IDWValidation';
            let status: number;

            this.httpClient.post(url, realPayload, { headers, params })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    status = resp.status;
                    if (status === 202) {
                        const finalResp = await this._pollForCompletion(resp, resourceType);
                        const body = finalResp.json();
                        if (body && body.status === 'Succeeded') {
                            const result = this._getExecuteChangeResult('databases', name);
                            try {
                                if (payload.callback) {
                                    // post update issue would not influence the publish result. The post update issue would be handled in callback function
                                    await this._postUpdateDatabase(payload, name);
                                }
                            } catch (error) {
                                this.loggerHelper.logRequestWarning(this.className, '_updateDatabase: Error while handling the response', error, { url });
                                await this._handleEntityListError(resourceType, error);
                            }
                            resolve(result);
                        } else {
                            throw finalResp.json();
                        }
                    } else {
                        throw resp.json();
                    }
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, '_updateDatabase: Error while calling url', reason, { url });
                    status = reason.status;
                    reject(reason);
                })
                .then(() => {
                    this.logger.logEvent(new CrudEvent(action, name || this.urlService.resourceName, resourceType, '', undefined, status.toString()));
                    // Maxium length of value for the custom properties in telemetry is 8912 characters and when exceeded, the value is truncated.
                    // To avoid such data trimming, pushing into multiple keys by splitting tables data.
                    const noOfChunksToSplitTables = 50;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const telemetryPayloadProperties: Record<string, any> = {
                        database: payload.telemetryPayload.database
                    };
                    let tablesCounter = 1;
                    do {
                        telemetryPayloadProperties[`tables_${tablesCounter}`] = payload.telemetryPayload.tables.splice(0, noOfChunksToSplitTables);
                        tablesCounter++;
                    } while (payload.telemetryPayload.tables.length > 0);

                    this.logger.logEvent(new EventRow('Idw.Customization.PublishDatabaseDetails', telemetryPayloadProperties));
                });
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _dummyUpdateEntitiesInDatabase(resourceType: Constants.ResourceType, name: string | null, payload: any): Promise<{}> {
        return new Promise<IAzureResource>(async (resolve, reject) => {
            const result = this._getExecuteChangeResult(`databases/${payload.databaseName}/${resourceType}s`, name);
            resolve(result);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _getExecuteChangeResult(idInfo: string, name: string): IAzureResource {
        const resourceIdPrefix = `${this._workspaceId}/${idInfo}/`;
        return {
            id: `${resourceIdPrefix}${name}`,
            properties: '',
            name
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _postUpdateDatabase(payload: any, name: string): Promise<void> {
        if (!payload.callback) {
            return;
        }

        const updateDDLItem = (resource: IAzureResource, item: IDDLItem): IDDLItem => {
            return {
                ...item,
                ObjectVersion: resource.properties.ObjectVersion,
                ObjectId: resource.properties.ObjectId
            };
        };

        // fetch the latest objectId and objectVersion
        const databasePromise: Promise<IAzureResource> = this._getDatabaseEntity('database', name);
        const listEntitiesPromises: Array<Promise<IAzureResource[] | IAzureResource>> = [
            this._listDatabasesOrEntities('table', `databases/${name}/table`),
            this._listDatabasesOrEntities('relationship', `databases/${name}/relationship`)
        ];

        // get updated nameds info
        const updatedItemNames: string[] = [];
        const updatedItems: IDDLItem[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload.payload.Ddls.forEach((item: any) => {
            // the format of key should respect the one set in payload
            updatedItemNames.push(item.NewEntity.Name);
            updatedItems.push({
                DDLType: item.ActionType,
                EntityName: item.NewEntity.Name,
                EntityType: item.NewEntity.EntityType,
                PublishStatus: 'PUBLISHED',
                ObjectId: '',
                ObjectVersion: 1
            });
        });

        const databaseResource = await databasePromise;
        const resourcesList = await Promise.all(listEntitiesPromises);

        const finalDDLItems: IDDLItem[] = [];
        // generate database ddl item
        const updatedDBItem = updatedItems.find((item: IDDLItem) => item.EntityType === 'DATABASE');
        if (updatedDBItem) {
            const dbDDLItem: IDDLItem = updateDDLItem(databaseResource, updatedDBItem);
            finalDDLItems.push(dbDDLItem);
        }
        // generate database entities's ddl item
        resourcesList.forEach((resources: IAzureResource[]) => {
            const filteredResources = resources.filter((resource: IAzureResource) => updatedItemNames.includes(resource.name));
            filteredResources.forEach((resource: IAzureResource) => {
                const filteredItem = updatedItems.find((item: IDDLItem) => resource.name === item.EntityName);
                if (filteredItem) {
                    const ddlItem = updateDDLItem(resource, filteredItem);
                    finalDDLItems.push(ddlItem);
                }
            });
        });

        payload.callback(finalDDLItems);
        this._refreshDatabaseOE(name);
    }

    /**
     * Fetch a specific entity in the database or database itself
     * @param resourceType basic resource type, for example, database, table, relationship
     * @param name the name of the specific resource would like to fetch
     * @param complexResourceType needed when fetching entity in the database. Format: databases/{databaseName}/table
     * @returns Resource returned from backend
     */
    private _getDatabaseEntity(resourceType: Constants.ResourceType, name: string, complexResourceType?: string): Promise<IAzureResource> {
        return new Promise<IAzureResource>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = `${this._createResourceBaseUri(complexResourceType ? complexResourceType : resourceType)}/${name}`;
            this.httpClient.get(url, { headers, params: this.getDefaultParams(null, this._getApiVersion(resourceType)) })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    resolve(resp.json());
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, '_getDatabaseEntity: Error while calling url', reason, { getDatabaseEntity: url });
                    reject(reason);
                });
        });
    }

    private async _refreshDatabaseOE(databaseName: string): Promise<void> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const conceptTreeService = await getServiceInstance<any>(this._contextData, 'ConceptTreeService');
        const conceptTree = conceptTreeService.getConceptTree('databases');
        if (conceptTree) {
            const databaseNode = await RootApplication.injector.get(NgZone).run(async () => {
                const expandedSuccessfulFlag = await conceptTree.expandPath(databaseName);
                if (expandedSuccessfulFlag) {
                    return await conceptTree.getTreeItemByPath(databaseName);
                }
                return undefined;
            });
            if (databaseNode) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const treeDataProviderManager = await getServiceInstance<any>(this._contextData, 'TreeDataProviderManager');
                // moved logic from TreeService._refresh.
                return await RootApplication.injector.get(NgZone).run(async () => {
                    await treeDataProviderManager.updateData('databases', databaseNode);
                });
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async _handleEntityListError(resourceType: string, error: any): Promise<void> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const serverErrorCollectionService = await getServiceInstance<any>(this._contextData, 'ServerErrorCollectionService');
        return serverErrorCollectionService.addItemAndShow(resourceType, error, stringResources.FailedToLoadPostPublish);
    }
}
